import { forwardRef } from 'react';
import { FilePond as BaseFilePond, registerPlugin, type FilePondProps } from 'react-filepond';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import type { FilePondFile } from 'filepond';

import filepondCSS from 'filepond/dist/filepond.min.css?inline';
import filepondImagePreviewCSS from 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css?inline';

import { injectStyles, insertStylesToHead, makeStyleTag } from '@lib/utils/dom/injectStylesToHead';
import { cn } from '@lib/utils';

// ----------------------------------------------------------------------

registerPlugin(FilePondPluginImagePreview, FilePondPluginFileValidateType);

// Inject base FilePond vendor styles (theme styles are in filepond.css)
const styles = `@layer vendor {
  ${filepondCSS} ${filepondImagePreviewCSS}
}`;

const sheet = makeStyleTag();
injectStyles(sheet, styles);
insertStylesToHead(sheet);

// ----------------------------------------------------------------------

type FilepondProps = {
  className?: string;
  classNames?: {
    root?: string;
    filepond?: string;
  };
  /** Show dashed border style (default: true) */
  bordered?: boolean;
  /** Show filled background (default: false) */
  filled?: boolean;
  /** Number of columns in grid layout (default: 1, no grid) */
  grid?: number;
  style?: React.CSSProperties;
} & FilePondProps;

type FilepondRef = BaseFilePond;

const Filepond = forwardRef<BaseFilePond, FilepondProps>(
  ({ className, classNames, bordered = true, filled = false, grid = 1, style, ...rest }, ref) => {
    return (
      <div
        className={cn(
          bordered && 'fp-bordered',
          filled && 'fp-filled',
          grid > 1 && 'fp-grid',
          className,
          classNames?.root
        )}
        style={{ '--fp-grid': grid, ...style } as React.CSSProperties}
      >
        <BaseFilePond className={classNames?.filepond} ref={ref} {...rest} />
      </div>
    );
  }
);

Filepond.displayName = 'FilePond';

// Re-export types for convenience
export type { FilePondFile };

export { Filepond, registerPlugin };
export type { FilepondProps, FilepondRef };
